import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Icon,
  Alert,
  AlertIcon,
  Progress
} from '@chakra-ui/react';
import {
  FaWallet,
  FaClock,
  FaDollarSign,
  FaChartLine
} from 'react-icons/fa';
import { useWalletContext } from '../../context/WalletContext';

const ViewOnlyWalletSummary = () => {
  const { walletData, formatCurrency } = useWalletContext();

  if (!walletData) {
    return null;
  }

  const summary = walletData.summary || {};
  const viewOnlyContext = walletData.view_only_context || {};
  const isLinkedArtist = viewOnlyContext.wallet_type === 'linked_artist';

  const StatCard = ({ icon, label, value, helpText, color = "primary" }) => (
    <Box bg="dark.500" borderRadius="lg" p={4} border="1px solid" borderColor="dark.300">
      <Stat>
        <StatLabel color="gray.400" fontSize="sm">
          <HStack spacing={2}>
            <Icon as={icon} color={`${color}.400`} boxSize={4} />
            <Text>{label}</Text>
          </HStack>
        </StatLabel>
        <StatNumber color="white" fontSize="xl">
          {value}
        </StatNumber>
        {helpText && (
          <StatHelpText color="gray.500" fontSize="xs">
            {helpText}
          </StatHelpText>
        )}
      </Stat>
    </Box>
  );

  return (
    <VStack spacing={6} align="stretch">
      {/* Info Alert for Linked Artists */}
      {isLinkedArtist && (
        <Alert status="info" bg="blue.900" borderColor="blue.600" borderRadius="md">
          <AlertIcon color="blue.300" />
          <VStack align="start" spacing={1} flex={1}>
            <Text fontSize="sm" color="blue.200" fontWeight="medium">
              Linked Artist Account
            </Text>
            <Text fontSize="xs" color="blue.300">
              Your wallet is managed by your merchant partner.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Balance Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        <StatCard
          icon={FaDollarSign}
          label="Available Balance"
          value={formatCurrency(summary.available_balance || 0)}
          helpText="Ready to spend"
          color="green"
        />

        <StatCard
          icon={FaClock}
          label="Pending Earnings"
          value={formatCurrency(summary.pending_earnings || 0)}
          helpText="Processing for transfer"
          color="yellow"
        />

        <StatCard
          icon={FaChartLine}
          label="Total Earned"
          value={formatCurrency(summary.total_earned || 0)}
          helpText="All-time earnings"
          color="blue"
        />

        <StatCard
          icon={FaWallet}
          label="Total Spent"
          value={formatCurrency(summary.total_spent || 0)}
          helpText="Purchase history"
          color="purple"
        />
      </SimpleGrid>

      {/* Earnings Breakdown */}
      {(summary.total_sale_earnings > 0 || summary.total_referral_earnings > 0) && (
        <Box bg="dark.500" borderRadius="lg" p={5} border="1px solid" borderColor="dark.300">
          <VStack spacing={4} align="stretch">
            <Text fontWeight="bold" color="white" fontSize="md">
              Earnings Breakdown
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text color="gray.300" fontSize="sm">Music Sales</Text>
                  <Text color="white" fontSize="sm" fontWeight="medium">
                    {formatCurrency(summary.total_sale_earnings || 0)}
                  </Text>
                </HStack>
                <Progress
                  value={((summary.total_sale_earnings || 0) / (summary.total_earned || 1)) * 100}
                  colorScheme="blue"
                  bg="dark.300"
                  size="sm"
                  borderRadius="full"
                />
              </Box>

              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text color="gray.300" fontSize="sm">Referrals</Text>
                  <Text color="white" fontSize="sm" fontWeight="medium">
                    {formatCurrency(summary.total_referral_earnings || 0)}
                  </Text>
                </HStack>
                <Progress
                  value={((summary.total_referral_earnings || 0) / (summary.total_earned || 1)) * 100}
                  colorScheme="purple"
                  bg="dark.300"
                  size="sm"
                  borderRadius="full"
                />
              </Box>
            </SimpleGrid>
          </VStack>
        </Box>
      )}

      {/* Restrictions Notice */}
      <Alert status="warning" bg="yellow.900" borderColor="yellow.600" borderRadius="lg">
        <AlertIcon color="yellow.300" />
        <VStack align="start" spacing={1} flex={1}>
          <Text fontSize="sm" color="yellow.200" fontWeight="medium">
            Limited Access Mode
          </Text>
          <Text fontSize="xs" color="yellow.300">
            {isLinkedArtist
              ? "Fund management is handled by your merchant partner. Contact them for deposits or withdrawals."
              : "You can view your wallet statistics but cannot perform transactions in this mode."
            }
          </Text>
        </VStack>
      </Alert>
    </VStack>
  );
};

export default ViewOnlyWalletSummary;